import React, { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useListOnboardingReviews,
  useReviewOnboarding,
  getListOnboardingReviewsQueryKey,
  useGetOnboardingMsa,
  useCountersignOnboardingMsa,
  getGetOnboardingMsaQueryKey,
  useGetMe,
  useListAreas,
  useListRegions,
  useListAccountManagers,
  useListMasterDealers,
  type OnboardingReview,
  type OnboardingStatus,
  type OnboardingDocumentReviewInput,
} from "@workspace/api-client-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { StatusBadge } from "@/components/StatusBadge";
import { AuthenticatedDocumentLink } from "@/components/AuthenticatedDocumentLink";
import { MsaContractDocument } from "@/components/onboarding/MsaContractDocument";
import { SignaturePad } from "@/components/onboarding/SignaturePad";
import { useToast } from "@/hooks/use-toast";
import {
  Check,
  Clock,
  FileText,
  Loader2,
  PenLine,
  RotateCcw,
  ShieldCheck,
  UserRound,
  X,
} from "lucide-react";
import {
  ONBOARDING_DOCUMENT_KEYS,
  DOCUMENT_LABELS,
  DOCUMENT_PATH_FIELDS,
  REJECTION_REASONS,
  rejectionReasonLabel,
  type OnboardingDocumentKey,
} from "@/lib/ficaReview";

const AGENT_TYPE_LABELS: Record<string, string> = {
  field_sales: "Independent Field Sales Agent",
  independent_technician: "Independent Technician Referral Agent",
};

const FILTERS: { label: string; value: OnboardingStatus | "all" }[] = [
  { label: "Awaiting review", value: "submitted" },
  { label: "Changes requested", value: "changes_requested" },
  { label: "Approved", value: "approved" },
  { label: "Rejected", value: "rejected" },
  { label: "All", value: "all" },
];

function agentTypeLabel(value: string | null | undefined): string {
  if (!value) return "—";
  return AGENT_TYPE_LABELS[value] ?? value;
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
}

function apiErrorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === "object" && "data" in err) {
    const data = (err as { data?: unknown }).data;
    if (data && typeof data === "object") {
      const message =
        (data as { message?: unknown }).message ??
        (data as { error?: unknown }).error;
      if (typeof message === "string" && message.trim()) return message;
    }
  }
  return fallback;
}

function ApplicantAvatar({
  name,
  photoUrl,
}: {
  name: string;
  photoUrl?: string | null;
}) {
  if (photoUrl) {
    return (
      <img
        src={`/api/storage${photoUrl}`}
        alt={name}
        className="w-9 h-9 rounded-full object-cover border border-gray-200"
      />
    );
  }
  return (
    <div className="w-9 h-9 rounded-full bg-[#0B132B]/5 border border-gray-200 flex items-center justify-center text-xs font-semibold text-[#0B132B]">
      {initials(name) || <UserRound className="w-4 h-4 text-gray-400" />}
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2 border-b border-gray-50 last:border-0">
      <span className="text-sm text-gray-500 shrink-0">{label}</span>
      <span className="text-sm font-medium text-[#0B132B] text-right">
        {value || "—"}
      </span>
    </div>
  );
}

type DocDecisionStatus = "pending" | "accepted" | "rejected";

type DocDecisionState = {
  status: DocDecisionStatus;
  rejectionReason: string;
  comment: string;
};

function emptyDecision(): DocDecisionState {
  return { status: "pending", rejectionReason: "", comment: "" };
}

// Direct (non-OpenAPI) binary endpoint that streams a FICA document. Authorized
// by the reviewer's role, not document ownership, so managers/admins can view
// applicant uploads via authenticated fetch (Bearer token).
function documentUrl(reviewId: number, key: OnboardingDocumentKey): string {
  return `/api/onboarding-reviews/${reviewId}/documents/${key}`;
}

function DocDecisionBadge({
  uploaded,
  status,
}: {
  uploaded: boolean;
  status: DocDecisionStatus | null;
}) {
  if (!uploaded) {
    return (
      <span className="text-xs font-medium text-red-700">Not uploaded</span>
    );
  }
  if (status === "accepted") {
    return <span className="text-xs font-medium text-[#00A859]">Accepted</span>;
  }
  if (status === "rejected") {
    return <span className="text-xs font-medium text-red-700">Rejected</span>;
  }
  return <span className="text-xs font-medium text-amber-600">Pending</span>;
}

// Editable per-document card shown while an application is awaiting review.
function DocumentReviewCard({
  documentKey,
  uploaded,
  url,
  decision,
  onChange,
}: {
  documentKey: OnboardingDocumentKey;
  uploaded: boolean;
  url: string;
  decision: DocDecisionState;
  onChange: (next: DocDecisionState) => void;
}) {
  const label = DOCUMENT_LABELS[documentKey];
  if (!uploaded) {
    return (
      <div className="flex items-center justify-between p-3 border-b border-gray-100 last:border-0">
        <span className="text-sm font-medium text-[#0B132B]">{label}</span>
        <span className="text-xs font-medium text-red-700">Not uploaded</span>
      </div>
    );
  }
  return (
    <div className="p-3 border-b border-gray-100 last:border-0 space-y-3">
      <div className="flex items-start gap-3">
        <AuthenticatedDocumentLink apiPath={url} variant="thumbnail" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-medium text-[#0B132B]">{label}</span>
            <AuthenticatedDocumentLink apiPath={url} label="Open" />
          </div>
          <div className="mt-2 flex gap-1.5">
            <button
              type="button"
              onClick={() => onChange({ ...decision, status: "accepted" })}
              className={
                decision.status === "accepted"
                  ? "inline-flex items-center rounded-md bg-[#00A859] px-2.5 py-1 text-xs font-medium text-white"
                  : "inline-flex items-center rounded-md border border-gray-200 px-2.5 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50"
              }
            >
              <Check className="w-3.5 h-3.5 mr-1" /> Accept
            </button>
            <button
              type="button"
              onClick={() => onChange({ ...decision, status: "rejected" })}
              className={
                decision.status === "rejected"
                  ? "inline-flex items-center rounded-md bg-red-600 px-2.5 py-1 text-xs font-medium text-white"
                  : "inline-flex items-center rounded-md border border-gray-200 px-2.5 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50"
              }
            >
              <X className="w-3.5 h-3.5 mr-1" /> Reject
            </button>
          </div>
        </div>
      </div>
      {decision.status === "rejected" ? (
        <div className="space-y-2 sm:pl-[76px]">
          <Select
            value={decision.rejectionReason}
            onValueChange={(value) =>
              onChange({ ...decision, rejectionReason: value })
            }
          >
            <SelectTrigger className="h-8 text-sm">
              <SelectValue placeholder="Select a rejection reason" />
            </SelectTrigger>
            <SelectContent>
              {REJECTION_REASONS.map((r) => (
                <SelectItem key={r.value} value={r.value}>
                  {r.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Textarea
            value={decision.comment}
            onChange={(e) => onChange({ ...decision, comment: e.target.value })}
            placeholder="Add a comment for the agent (required)."
            rows={2}
            className="text-sm"
          />
        </div>
      ) : null}
    </div>
  );
}

// Read-only per-document outcome list shown once an application is decided.
function DecidedDocumentList({ review }: { review: OnboardingReview }) {
  const byKey = new Map(
    review.documentReviews.map((d) => [d.documentKey, d] as const),
  );
  return (
    <div className="rounded-xl border border-gray-100">
      <div className="px-3 py-2 bg-gray-50 border-b border-gray-100">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          FICA documents
        </p>
      </div>
      <div className="px-3">
        {ONBOARDING_DOCUMENT_KEYS.map((key) => {
          const uploaded = !!review[DOCUMENT_PATH_FIELDS[key]];
          const dr = byKey.get(key);
          return (
            <div
              key={key}
              className="py-2 border-b border-gray-50 last:border-0"
            >
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm text-gray-500">
                  {DOCUMENT_LABELS[key]}
                </span>
                <div className="flex items-center gap-3">
                  {uploaded ? (
                    <AuthenticatedDocumentLink
                      apiPath={documentUrl(review.id, key)}
                      label="Open"
                    />
                  ) : null}
                  <DocDecisionBadge
                    uploaded={uploaded}
                    status={dr ? dr.status : null}
                  />
                </div>
              </div>
              {dr?.status === "rejected" ? (
                <div className="mt-1 text-xs text-red-700">
                  {rejectionReasonLabel(dr.rejectionReason)}
                  {dr.comment ? (
                    <span className="text-gray-500"> — {dr.comment}</span>
                  ) : null}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ReviewDialog({
  review,
  onClose,
}: {
  review: OnboardingReview;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { mutateAsync, isPending } = useReviewOnboarding();
  const [notes, setNotes] = useState("");

  // Approval assignment (captured at approval and written onto the agent). The
  // server hard-gates approval on area + account manager + a non-empty OpenServe
  // code; the master dealer defaults to the house dealer (Mooya Wireless).
  const { data: areas = [] } = useListAreas();
  const { data: regions = [] } = useListRegions();
  const { data: accountManagers = [] } = useListAccountManagers();
  const { data: dealersPage } = useListMasterDealers();
  const dealers = useMemo(() => dealersPage?.items ?? [], [dealersPage]);

  const [areaId, setAreaId] = useState<number | null>(null);
  const [masterDealerId, setMasterDealerId] = useState<number | null>(null);
  const [accountManagerId, setAccountManagerId] = useState<number | null>(null);
  const [openserveCode, setOpenserveCode] = useState("");
  const [confirmOpenserveCode, setConfirmOpenserveCode] = useState("");

  // Default the master dealer to the house dealer (Mooya Wireless) once the list
  // loads, unless the reviewer already picked one. If it is somehow missing we
  // leave it unset (fail closed) so Approve stays blocked.
  useEffect(() => {
    if (masterDealerId != null || dealers.length === 0) return;
    const mooya = dealers.find(
      (d) => d.name.trim().toLowerCase() === "mooya wireless",
    );
    if (mooya) setMasterDealerId(mooya.id);
  }, [dealers, masterDealerId]);

  // Picking an area auto-suggests the account manager who owns that area's
  // region (regions.accountManagerId). The reviewer can still override it.
  const handleAreaChange = (value: string) => {
    const id = Number(value);
    setAreaId(id);
    const regionId = areas.find((a) => a.id === id)?.regionId;
    const amId =
      regionId != null
        ? (regions.find((r) => r.id === regionId)?.accountManagerId ?? null)
        : null;
    if (amId != null) setAccountManagerId(amId);
  };

  // Full name must be at least a name + surname (>=2 words) — mirrors the server.
  const fullNameValid =
    (review.fullName ?? "").trim().split(/\s+/).filter(Boolean).length >= 2;
  const openserveValid = openserveCode.trim().length > 0;
  // Double-entry: the OpenServe code is the agent's ONE code (never generated), so
  // it must be typed identically twice before approval writes it to the agent.
  const openserveCodesMatch =
    openserveValid && openserveCode.trim() === confirmOpenserveCode.trim();
  const showCodeMismatch =
    confirmOpenserveCode.trim().length > 0 &&
    openserveCode.trim() !== confirmOpenserveCode.trim();
  const assignmentComplete =
    areaId != null &&
    masterDealerId != null &&
    accountManagerId != null &&
    openserveCodesMatch;
  // All non-document approval prerequisites (mirrors the server hard-gates).
  const approvalReady =
    assignmentComplete && review.phoneVerified && fullNameValid;

  // MSA gate: a field-sales application cannot be approved until the agreement is
  // signed by the agent AND counter-signed by an admin (enforced server-side).
  // Fail closed: until the status loads, assume a field-sales application still
  // needs signatures so Approve stays disabled (the server also hard-gates
  // approval with 400 msa_not_signed regardless of UI state).
  const { data: msa } = useGetOnboardingMsa(review.id);
  const msaRequired = msa?.required ?? review.agentType === "field_sales";
  const msaBlocksApproval = msaRequired && !(msa?.fullySigned ?? false);

  const decided = review.status !== "submitted";

  // FICA documents actually uploaded on this application — only these can be
  // reviewed, and every one must be accepted before approval.
  const presentKeys = useMemo(
    () =>
      ONBOARDING_DOCUMENT_KEYS.filter(
        (key) => !!review[DOCUMENT_PATH_FIELDS[key]],
      ),
    [review],
  );

  const [docDecisions, setDocDecisions] = useState<
    Record<OnboardingDocumentKey, DocDecisionState>
  >(() => {
    const initial = {} as Record<OnboardingDocumentKey, DocDecisionState>;
    for (const key of ONBOARDING_DOCUMENT_KEYS) initial[key] = emptyDecision();
    return initial;
  });

  const setDecision = (key: OnboardingDocumentKey, next: DocDecisionState) =>
    setDocDecisions((prev) => ({ ...prev, [key]: next }));

  const allAccepted =
    presentKeys.length > 0 &&
    presentKeys.every((key) => docDecisions[key].status === "accepted");

  // Every uploaded document must have a final decision before ANY outcome can
  // be recorded (mirrors the server). A `pending` document blocks every action.
  const allDecided =
    presentKeys.length > 0 &&
    presentKeys.every((key) => docDecisions[key].status !== "pending");

  // A rejected document is only complete with BOTH a reason and a comment.
  const rejectedDocsComplete = presentKeys.every((key) => {
    const d = docDecisions[key];
    return (
      d.status !== "rejected" ||
      (!!d.rejectionReason && d.comment.trim().length > 0)
    );
  });

  // Reject / Request changes are allowed once every document is decided and each
  // rejected document carries a reason + comment. Approval additionally requires
  // every document to be accepted (`allAccepted`).
  const canReject = allDecided && rejectedDocsComplete;

  // Send a decision for every uploaded document; the server rejects approval
  // unless all present documents are accepted in this payload.
  const buildDocumentReviews = (): OnboardingDocumentReviewInput[] =>
    presentKeys.map((key) => {
      const d = docDecisions[key];
      return {
        documentKey: key,
        status: d.status,
        rejectionReason:
          d.status === "rejected" && d.rejectionReason
            ? d.rejectionReason
            : null,
        comment: d.comment.trim() ? d.comment.trim() : null,
      };
    });

  const submitDecision = async (
    status: "approved" | "changes_requested" | "rejected",
  ) => {
    if (!allDecided) {
      toast({
        title: "Review every document",
        description: "Accept or reject each uploaded document first.",
        variant: "destructive",
      });
      return;
    }

    const documentReviews = buildDocumentReviews();
    const rejectedDocs = documentReviews.filter((d) => d.status === "rejected");

    if (rejectedDocs.some((d) => !d.rejectionReason)) {
      toast({
        title: "Rejection reason required",
        description: "Pick a reason for each rejected document.",
        variant: "destructive",
      });
      return;
    }

    if (rejectedDocs.some((d) => !d.comment)) {
      toast({
        title: "Comment required",
        description: "Add a comment for each rejected document.",
        variant: "destructive",
      });
      return;
    }

    if (status === "approved" && !allAccepted) {
      toast({
        title: "Documents not accepted",
        description: "Accept every uploaded document before approving.",
        variant: "destructive",
      });
      return;
    }

    if (status === "approved" && openserveValid && !openserveCodesMatch) {
      toast({
        title: "Codes do not match",
        description:
          "Enter the OpenServe dealer code twice and make sure both entries match before approving.",
        variant: "destructive",
      });
      return;
    }

    if (status === "approved" && !approvalReady) {
      toast({
        title: "Assignment incomplete",
        description:
          "Set the area, master dealer, account manager and OpenServe code. The applicant must also have an OTP-verified mobile and a full name (name + surname).",
        variant: "destructive",
      });
      return;
    }

    if (
      (status === "rejected" || status === "changes_requested") &&
      !notes.trim() &&
      rejectedDocs.length === 0
    ) {
      toast({
        title: "Feedback required",
        description:
          "Add a note or reject at least one document with a reason.",
        variant: "destructive",
      });
      return;
    }
    try {
      await mutateAsync({
        id: review.id,
        data: {
          status,
          reviewerNotes: notes.trim() ? notes.trim() : null,
          documentReviews,
          ...(status === "approved"
            ? {
                assignment: {
                  areaId: areaId ?? undefined,
                  masterDealerId: masterDealerId ?? undefined,
                  accountManagerId: accountManagerId ?? undefined,
                  openserveDealerCode: openserveCode.trim(),
                },
              }
            : {}),
        },
      });
      queryClient.invalidateQueries({
        queryKey: getListOnboardingReviewsQueryKey(),
      });
      toast({
        title:
          status === "approved"
            ? "Application approved"
            : status === "changes_requested"
              ? "Changes requested"
              : "Application rejected",
        description: `${review.applicantName} has been notified by email.`,
      });
      onClose();
    } catch (err) {
      toast({
        title: "Could not save decision",
        description: apiErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open onOpenChange={(open) => (!open ? onClose() : undefined)}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-[#0B132B]">
            {review.applicantName}
          </DialogTitle>
          <DialogDescription>{review.applicantEmail}</DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2">
          <StatusBadge status={review.status} />
          {review.submittedAt ? (
            <span className="text-xs text-gray-500">
              Submitted {new Date(review.submittedAt).toLocaleDateString()}
            </span>
          ) : null}
        </div>

        {review.reviewerNotes ? (
          <div className="rounded-lg bg-gray-50 border border-gray-100 p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
              Previous feedback
            </p>
            <p className="text-sm text-[#0B132B] whitespace-pre-line">
              {review.reviewerNotes}
            </p>
          </div>
        ) : null}

        <div className="rounded-xl border border-gray-100">
          <div className="px-3 py-2 bg-gray-50 border-b border-gray-100">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Applicant details
            </p>
          </div>
          <div className="px-3">
            <DetailRow label="Full name" value={review.fullName ?? ""} />
            <DetailRow label="ID number" value={review.idNumber ?? ""} />
            <DetailRow label="Mobile" value={review.phone ?? ""} />
            <DetailRow
              label="Address"
              value={[
                review.addressLine,
                review.city,
                review.province,
                review.postalCode,
              ]
                .filter(Boolean)
                .join(", ")}
            />
            <DetailRow
              label="Agent type"
              value={agentTypeLabel(review.agentType)}
            />
          </div>
        </div>

        <div className="rounded-xl border border-gray-100">
          <div className="px-3 py-2 bg-gray-50 border-b border-gray-100">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Banking
            </p>
          </div>
          <div className="px-3">
            <DetailRow label="Bank" value={review.bankName ?? ""} />
            <DetailRow label="Account" value={review.accountNumber ?? ""} />
            <DetailRow label="Account type" value={review.accountType ?? ""} />
            <DetailRow label="Branch code" value={review.branchCode ?? ""} />
          </div>
        </div>

        <MsaCountersignSection onboardingId={review.id} />

        {decided ? (
          <>
            <DecidedDocumentList review={review} />
            <p className="text-sm text-gray-500">
              This application has already been reviewed.
            </p>
          </>
        ) : (
          <>
            <div className="rounded-xl border border-gray-100">
              <div className="px-3 py-2 bg-gray-50 border-b border-gray-100">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Approval prerequisites
                </p>
              </div>
              <div className="px-3 py-2.5 space-y-1.5">
                <div className="flex items-center gap-2 text-sm">
                  {review.phoneVerified ? (
                    <Check className="w-4 h-4 shrink-0 text-[#00A859]" />
                  ) : (
                    <X className="w-4 h-4 shrink-0 text-red-600" />
                  )}
                  <span
                    className={
                      review.phoneVerified ? "text-[#0B132B]" : "text-red-700"
                    }
                  >
                    Mobile verified by OTP
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  {fullNameValid ? (
                    <Check className="w-4 h-4 shrink-0 text-[#00A859]" />
                  ) : (
                    <X className="w-4 h-4 shrink-0 text-red-600" />
                  )}
                  <span
                    className={
                      fullNameValid ? "text-[#0B132B]" : "text-red-700"
                    }
                  >
                    Full name (name + surname)
                  </span>
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-gray-100">
              <div className="px-3 py-2 bg-gray-50 border-b border-gray-100">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Assignment
                </p>
              </div>
              <div className="px-3 py-3 space-y-3">
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-[#0B132B]">
                    Area
                  </Label>
                  <Select
                    value={areaId != null ? String(areaId) : undefined}
                    onValueChange={handleAreaChange}
                  >
                    <SelectTrigger className="h-9 text-sm">
                      <SelectValue placeholder="Select an area" />
                    </SelectTrigger>
                    <SelectContent>
                      {areas.map((a) => (
                        <SelectItem key={a.id} value={String(a.id)}>
                          {a.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-[#0B132B]">
                    Master dealer
                  </Label>
                  <Select
                    value={
                      masterDealerId != null ? String(masterDealerId) : undefined
                    }
                    onValueChange={(v) => setMasterDealerId(Number(v))}
                  >
                    <SelectTrigger className="h-9 text-sm">
                      <SelectValue placeholder="Select a master dealer" />
                    </SelectTrigger>
                    <SelectContent>
                      {dealers.map((d) => (
                        <SelectItem key={d.id} value={String(d.id)}>
                          {d.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-[#0B132B]">
                    Account manager
                  </Label>
                  <Select
                    value={
                      accountManagerId != null
                        ? String(accountManagerId)
                        : undefined
                    }
                    onValueChange={(v) => setAccountManagerId(Number(v))}
                  >
                    <SelectTrigger className="h-9 text-sm">
                      <SelectValue placeholder="Select an account manager" />
                    </SelectTrigger>
                    <SelectContent>
                      {accountManagers.map((m) => (
                        <SelectItem key={m.id} value={String(m.id)}>
                          {m.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-[#0B132B]">
                    OpenServe dealer code
                  </Label>
                  <Input
                    value={openserveCode}
                    onChange={(e) => setOpenserveCode(e.target.value)}
                    placeholder="Copied from the OpenServe portal"
                    className="h-9 text-sm"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-[#0B132B]">
                    Confirm OpenServe dealer code
                  </Label>
                  <Input
                    value={confirmOpenserveCode}
                    onChange={(e) => setConfirmOpenserveCode(e.target.value)}
                    placeholder="Re-enter the code to confirm"
                    className="h-9 text-sm"
                  />
                  {showCodeMismatch ? (
                    <p className="text-xs text-red-600">
                      The codes do not match.
                    </p>
                  ) : null}
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-gray-100">
              <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b border-gray-100">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  FICA documents
                </p>
                <span className="text-xs text-gray-400">
                  Accept or reject each document
                </span>
              </div>
              <div>
                {ONBOARDING_DOCUMENT_KEYS.map((key) => (
                  <DocumentReviewCard
                    key={key}
                    documentKey={key}
                    uploaded={!!review[DOCUMENT_PATH_FIELDS[key]]}
                    url={documentUrl(review.id, key)}
                    decision={docDecisions[key]}
                    onChange={(next) => setDecision(key, next)}
                  />
                ))}
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium text-[#0B132B]">
                Feedback notes
                <span className="text-gray-400 font-normal">
                  {" "}
                  (required to reject or request changes, unless a document is
                  rejected)
                </span>
              </label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Explain what the agent should fix, or leave a note on approval."
                rows={3}
              />
            </div>

            {!allDecided ? (
              <p className="text-xs text-amber-600">
                Accept or reject every uploaded document to record a decision.
              </p>
            ) : !rejectedDocsComplete ? (
              <p className="text-xs text-amber-600">
                Add a reason and comment for every rejected document.
              </p>
            ) : !allAccepted ? (
              <p className="text-xs text-amber-600">
                Approval is disabled until every uploaded document is accepted.
              </p>
            ) : msaBlocksApproval ? (
              <p className="text-xs text-amber-600">
                Counter-sign the Field Sales Agent Agreement above before
                approving.
              </p>
            ) : !approvalReady ? (
              <p className="text-xs text-amber-600">
                Set the area, master dealer, account manager and OpenServe code,
                and confirm the applicant has an OTP-verified mobile and a full
                name, before approving.
              </p>
            ) : null}

            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button
                variant="outline"
                disabled={isPending || !canReject}
                onClick={() => submitDecision("rejected")}
                className="border-red-200 text-red-700 hover:bg-red-50"
              >
                <X className="w-4 h-4 mr-1.5" /> Reject
              </Button>
              <Button
                variant="outline"
                disabled={isPending || !canReject}
                onClick={() => submitDecision("changes_requested")}
                className="border-amber-200 text-amber-700 hover:bg-amber-50"
              >
                <RotateCcw className="w-4 h-4 mr-1.5" /> Request changes
              </Button>
              <Button
                disabled={
                  isPending ||
                  !allAccepted ||
                  msaBlocksApproval ||
                  !approvalReady
                }
                onClick={() => submitDecision("approved")}
                className="bg-[#00A859] hover:bg-[#00914c] text-white"
              >
                {isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-1.5" /> Approve
                  </>
                )}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function formatSignDate(epochMs: number | null | undefined): string {
  if (!epochMs) return "";
  return new Date(epochMs).toLocaleString("en-ZA", {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Africa/Johannesburg",
  });
}

function MsaSignatureRow({
  label,
  signed,
  name,
  at,
  image,
  ip,
  pendingLabel,
}: {
  label: string;
  signed: boolean;
  name: string | null | undefined;
  at: number | null | undefined;
  image?: string | null;
  ip?: string | null;
  pendingLabel: string;
}) {
  const signedOn = formatSignDate(at);
  return (
    <div className="flex items-start justify-between gap-4">
      <span className="text-sm text-gray-500 shrink-0">{label}</span>
      {signed ? (
        <span className="text-sm font-medium text-[#0B132B] text-right">
          {image ? (
            <img
              src={image}
              alt={`Signature of ${name ?? "signatory"}`}
              className="ml-auto mb-1 h-12 w-auto max-w-[180px] object-contain"
            />
          ) : null}
          {name || "Signed"}
          {signedOn ? (
            <span className="block text-xs font-normal text-gray-400">
              {signedOn}
            </span>
          ) : null}
          {ip ? (
            <span className="block text-[11px] font-normal text-gray-400">
              IP {ip}
            </span>
          ) : null}
        </span>
      ) : (
        <span className="inline-flex items-center gap-1.5 text-sm font-medium text-amber-600 text-right">
          <Clock className="w-3.5 h-3.5" /> {pendingLabel}
        </span>
      )}
    </div>
  );
}

// Admin counter-signature surface for the Field Sales Agent Agreement. Rendered
// inside the review dialog; only shows for MSA-required (field-sales)
// applications. The agent signs first; an admin then counter-signs here to
// complete the agreement and unblock approval.
function MsaCountersignSection({ onboardingId }: { onboardingId: number }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { data: msa } = useGetOnboardingMsa(onboardingId);
  const { data: me } = useGetMe();
  const isAdmin = me?.role === "admin";
  const countersign = useCountersignOnboardingMsa();
  const [open, setOpen] = useState(false);
  const [signatureImage, setSignatureImage] = useState<string | null>(null);
  const [consent, setConsent] = useState(false);

  useEffect(() => {
    if (open) {
      setSignatureImage(null);
      setConsent(false);
    }
  }, [open]);

  if (!msa?.required) return null;

  const docUrl = `/api/onboarding-reviews/${onboardingId}/msa/document`;
  const registeredName = me?.fullName?.trim() ?? "";
  const canSubmit = !!signatureImage && consent && !countersign.isPending;

  const submit = async () => {
    if (!canSubmit || !signatureImage) return;
    try {
      await countersign.mutateAsync({
        id: onboardingId,
        data: { signatureImage, consent },
      });
      await queryClient.invalidateQueries({
        queryKey: getGetOnboardingMsaQueryKey(onboardingId),
      });
      await queryClient.invalidateQueries({
        queryKey: getListOnboardingReviewsQueryKey(),
      });
      toast({
        title: "Agreement counter-signed",
        description: "The Field Sales Agent Agreement is now fully signed.",
      });
      setOpen(false);
    } catch (err) {
      toast({
        title: "Could not counter-sign",
        description: apiErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    }
  };

  return (
    <div className="rounded-xl border border-gray-100">
      <div className="px-3 py-2 bg-gray-50 border-b border-gray-100">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          Field Sales Agent Agreement
        </p>
      </div>
      <div className="px-3 py-3 space-y-3">
        <div className="max-h-[40vh] overflow-y-auto">
          <MsaContractDocument
            contract={msa.contract}
            prefill={msa.prefill}
            agentSignedName={msa.agentSignedName}
            agentSignedAt={msa.agentSignedAt}
            agentSignatureImage={msa.agentSignatureImage}
            agentSignedIp={msa.agentSignedIp}
            adminSignedName={msa.adminSignedName}
            adminSignedAt={msa.adminSignedAt}
            adminSignatureImage={msa.adminSignatureImage}
            adminSignedIp={msa.adminSignedIp}
          />
        </div>
        <MsaSignatureRow
          label="Agent signature"
          signed={msa.agentSigned}
          name={msa.agentSignedName}
          at={msa.agentSignedAt}
          image={msa.agentSignatureImage}
          ip={msa.agentSignedIp}
          pendingLabel="Awaiting agent signature"
        />
        <MsaSignatureRow
          label="Mooya counter-signature"
          signed={msa.adminSigned}
          name={msa.adminSignedName}
          at={msa.adminSignedAt}
          image={msa.adminSignatureImage}
          ip={msa.adminSignedIp}
          pendingLabel="Not yet counter-signed"
        />

        {msa.fullySigned ? (
          <div className="flex items-center gap-2 text-sm font-medium text-[#00A859]">
            <ShieldCheck className="w-4 h-4" /> Fully signed
          </div>
        ) : msa.agentSigned ? (
          isAdmin ? (
            <Button
              onClick={() => setOpen(true)}
              className="bg-[#E6007E] hover:bg-[#c4006b] text-white w-full"
            >
              <PenLine className="w-4 h-4 mr-1.5" /> Counter-sign agreement
            </Button>
          ) : (
            <p className="text-xs text-amber-600">
              The agent has signed. An admin must counter-sign to complete the
              agreement before it can be approved.
            </p>
          )
        ) : (
          <p className="text-xs text-amber-600">
            The agent must sign before the agreement can be counter-signed.
          </p>
        )}

        {msa.documentAvailable ? (
          <a
            href={docUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-[#0B132B] hover:underline"
          >
            <FileText className="w-4 h-4" /> View agreement
          </a>
        ) : null}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-[440px]">
          <DialogHeader>
            <DialogTitle>Counter-sign agreement</DialogTitle>
            <DialogDescription>
              You are counter-signing the Field Sales Agent Agreement on behalf
              of Mooya. This completes the agreement.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Draw your signature to counter-sign</Label>
              <p className="text-xs text-gray-500">
                Signing as{" "}
                <span className="font-semibold text-[#0B132B]">
                  {registeredName || "your registered name"}
                </span>{" "}
                — the name on your account.
              </p>
              <SignaturePad
                ariaLabel="Draw your counter-signature"
                onChange={setSignatureImage}
              />
            </div>
            <div className="flex items-start gap-2.5">
              <Checkbox
                id="cs-consent"
                checked={consent}
                onCheckedChange={(v) => setConsent(v === true)}
                className="mt-0.5"
              />
              <Label
                htmlFor="cs-consent"
                className="text-sm font-normal leading-snug text-gray-600 cursor-pointer"
              >
                I confirm I am authorised to counter-sign this agreement on
                behalf of Mooya, and I consent to signing it electronically.
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setOpen(false)}
              disabled={countersign.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={!canSubmit}
              className="bg-[#E6007E] hover:bg-[#c4006b] text-white"
            >
              {countersign.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                "Counter-sign"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function OnboardingReviewsList() {
  const [filter, setFilter] = useState<OnboardingStatus | "all">("submitted");
  const [active, setActive] = useState<OnboardingReview | null>(null);

  const params = useMemo(
    () => (filter === "all" ? undefined : { status: filter }),
    [filter],
  );
  const { data: reviews, isLoading, error } = useListOnboardingReviews(params);

  return (
    <>
      <Card className="border-0 shadow-sm">
        <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle className="text-lg text-[#0B132B]">
            Agent onboarding reviews
          </CardTitle>
          <div className="flex flex-wrap gap-1.5">
            {FILTERS.map((f) => (
              <button
                key={f.value}
                type="button"
                onClick={() => setFilter(f.value)}
                className={
                  filter === f.value
                    ? "px-3 py-1.5 rounded-lg text-sm font-medium bg-[#E6007E]/10 text-[#E6007E]"
                    : "px-3 py-1.5 rounded-lg text-sm font-medium text-gray-500 hover:text-[#0B132B] hover:bg-gray-50"
                }
              >
                {f.label}
              </button>
            ))}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 animate-pulse">
              <div className="h-64 bg-gray-100 rounded" />
            </div>
          ) : error || !reviews ? (
            <div className="p-6 text-red-500">Failed to load reviews.</div>
          ) : (
            <Table>
              <TableHeader className="bg-gray-50">
                <TableRow>
                  <TableHead className="w-14">Photo</TableHead>
                  <TableHead>Applicant</TableHead>
                  <TableHead>Agent type</TableHead>
                  <TableHead>Submitted</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reviews.map((review) => (
                  <TableRow key={review.id}>
                    <TableCell>
                      <ApplicantAvatar
                        name={review.applicantName}
                        photoUrl={review.profilePhotoPath}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="font-medium text-[#0B132B]">
                        {review.applicantName}
                      </div>
                      <div className="text-xs text-gray-500">
                        {review.applicantEmail}
                      </div>
                    </TableCell>
                    <TableCell>{agentTypeLabel(review.agentType)}</TableCell>
                    <TableCell>
                      {review.submittedAt
                        ? new Date(review.submittedAt).toLocaleDateString()
                        : "—"}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={review.status} />
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant={
                          review.status === "submitted" ? "default" : "outline"
                        }
                        className={
                          review.status === "submitted"
                            ? "bg-[#E6007E] hover:bg-[#c4006b] text-white"
                            : ""
                        }
                        onClick={() => setActive(review)}
                      >
                        {review.status === "submitted" ? "Review" : "View"}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {reviews.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="text-center p-8 text-gray-500"
                    >
                      No applications in this view.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {active ? (
        <ReviewDialog review={active} onClose={() => setActive(null)} />
      ) : null}
    </>
  );
}
